'use client';
import { brand } from '@lib/config/brand';
import { useEffect } from 'react';

/**
 * Listens for clicks on "Email us" links in the footer and navigates to a
 * `mailto:` link via client-side JS, instead of rendering it as a static href.
 *
 * Why: a static `mailto:` link in the server-rendered HTML causes Cloudflare's
 * Scrape Shield (email obfuscation) to rewrite the page and inject
 * `email-decode.min.js`, which becomes part of the page's critical request
 * chain (confirmed ~646ms of added latency in PageSpeed Insights). Deferring
 * the `mailto:` href to a click handler avoids the rewrite entirely.
 *
 * The design system's Footer component renders footer links from plain
 * {label, href} data and does not support attaching a per-link onClick
 * handler, so we implement this as a delegated document click listener
 * instead (mirrors use-footer-cookie-listener.ts). The listener runs in
 * the capture phase and swaps the anchor's href to a real `mailto:` link
 * just before the click completes, so the browser handles the navigation
 * itself (preserving Ctrl/Cmd-click, middle-click, and "open in new tab").
 *
 * The Footer's Link renders as `next/link`, whose own click handler runs
 * during the same target phase and — since `#open-email` is a local URL —
 * intercepts the click and client-side-routes to that fragment using its
 * own `href` prop, ignoring our DOM mutation. `stopPropagation()` here
 * (still in the capture phase, so it runs before Next's handler) prevents
 * that handler from ever seeing the click, leaving the browser's native
 * anchor navigation — using the href we just swapped in — to run.
 *
 * Usage: Call `useFooterEmailListener()` in a component that renders the footer.
 */
export function useFooterEmailListener() {
  useEffect(() => {
    if (!brand.email) {
      return;
    }

    const abortController = new AbortController();

    document.addEventListener(
      'click',
      (event) => {
        if (!(event.target instanceof Element)) {
          return;
        }

        const target = event.target.closest('a[href^="#open-email"]');
        if (target instanceof HTMLAnchorElement) {
          target.href = `mailto:${brand.email}`;
          event.stopPropagation();
        }
      },
      { signal: abortController.signal, capture: true },
    );

    return () => abortController.abort();
  }, []);
}
